import { useMemo, useState, useRef, useEffect } from "react";
import { Image, Pressable, StyleSheet, View, Animated, Easing } from "react-native";
import { ChevronDown, RefreshCw, Trash2 } from "lucide-react";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomText from "../../../common/general/CustomText/CustomText";
import Pill from "../../../common/Pill/Pill";
import INTEGRATIONS_ICONS, { type IntegrationIconKey } from "../../../../assets/integrations";
import { colors, radii, spacing, typographyScale } from "../../../../styles/design-system";
import resolveConnectorPermissions from "../../../../utils/integrations/permissionsResolver";

type StatusTone = "success" | "warning" | "error" | "neutral";

interface ConnectedServiceAccordionProps {
  serviceName: string;
  accountLabel: string;
  iconKey?: string | null;
  statusLabel?: string;
  statusTone?: StatusTone;
  connectedValue: string;
  lastSyncedValue: string;
  permissions: ReturnType<typeof resolveConnectorPermissions>;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onToggle?: (nextExpanded: boolean) => void;
  onDisconnect?: () => void;
  disconnectLabel?: string;
  onReconnect?: () => void;
  reconnectLabel?: string;
  isLoading?: boolean;
}

const LoadingSpinner = () => {
  const spinValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.loop(
      Animated.timing(spinValue, {
        toValue: 1,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    ).start();
  }, [spinValue]);

  const spin = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  });

  return (
    <Animated.View style={{ transform: [{ rotate: spin }] }}>
      <RefreshCw size={14} color={colors.white} />
    </Animated.View>
  );
};

const STATUS_STYLES: Record<StatusTone, { backgroundColor: string; textColor: string; borderColor: string }> = {
  success: {
    backgroundColor: colors.success[50],
    textColor: colors.success[700],
    borderColor: colors.success[400],
  },
  warning: {
    backgroundColor: colors.warning[50],
    textColor: colors.warning[700],
    borderColor: colors.warning[300],
  },
  error: {
    backgroundColor: colors.error[50],
    textColor: colors.error[700],
    borderColor: colors.error[300],
  },
  neutral: {
    backgroundColor: colors.grey[100],
    textColor: colors.grey[700],
    borderColor: colors.grey[300],
  },
};

const renderFallbackInitials = (serviceName: string) =>
  serviceName
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

export function ConnectedServiceAccordion({
  serviceName,
  accountLabel,
  iconKey,
  statusLabel = "Unknown",
  statusTone = "success",
  connectedValue,
  lastSyncedValue,
  permissions,
  expanded,
  defaultExpanded = false,
  onToggle,
  onDisconnect,
  disconnectLabel = "Disconnect",
  onReconnect,
  reconnectLabel,
  isLoading = false,
}: ConnectedServiceAccordionProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = expanded ?? internalExpanded;
  const statusStyle = STATUS_STYLES[statusTone] ?? STATUS_STYLES.neutral;
  const iconValue = iconKey ? INTEGRATIONS_ICONS[iconKey as IntegrationIconKey] : undefined;

  const permissionPills = useMemo(
    () =>
      permissions.map((permission) => (
        <Pill
          key={permission?.key}
          text={permission?.label}
          textSize="xs"
          backgroundColor={colors.grey[50]}
          textColor={colors.grey[700]}
          borderColor={colors.grey[200]}
          borderWidth={1}
          updated={true}
          containerStyle={styles.permissionPill}
          textStyle={styles.permissionText}
        />
      )),
    [permissions]
  );

  const handleToggle = () => {
    const nextExpanded = !isExpanded;
    if (expanded == null) {
      setInternalExpanded(nextExpanded);
    }
    onToggle?.(nextExpanded);
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  return (
    <View style={styles.card}>
      <Pressable
        onPress={handleToggle}
        accessibilityRole="button"
        accessibilityLabel={`${isExpanded ? "Collapse" : "Expand"} ${serviceName}`}
        accessibilityState={{ expanded: isExpanded }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={({ pressed }) => [styles.header, pressed && styles.headerPressed, isHovered && styles.headerHovered]}>
        <View style={styles.headerLeft}>
          <View style={styles.logoShell}>
            {iconValue ? (
              <Image source={{ uri: iconValue }} style={styles.logo} resizeMode="contain" />
            ) : (
              <View style={styles.fallbackLogo}>
                <CustomText
                  text={renderFallbackInitials(serviceName)}
                  useTranslationText={false}
                  style={styles.fallbackInitials}
                />
              </View>
            )}
          </View>
          <View style={styles.headerText}>
            <View style={styles.titleRow}>
              <CustomText text={serviceName} useTranslationText={false} style={styles.serviceName} numberOfLines={1} />
              <Pill
                text={statusLabel}
                textSize="xs"
                backgroundColor={statusStyle.backgroundColor}
                textColor={statusStyle.textColor}
                borderColor={statusStyle.borderColor}
                borderWidth={1}
                updated={true}
                containerStyle={styles.statusPill}
              />
            </View>
            <CustomText text={accountLabel} useTranslationText={false} style={styles.accountLabel} numberOfLines={1} />
          </View>
        </View>

        <View style={styles.headerActions}>
          {onReconnect ? (
            <CustomButton
              text={reconnectLabel ?? "Reconnect"}
              onPress={(event) => {
                event?.stopPropagation?.();
                onReconnect();
              }}
              useTranslationText={false}
              styleType="primary"
              size="s"
              disabled={isLoading}
              leftIcon={isLoading ? <LoadingSpinner /> : <RefreshCw size={14} color={colors.white} />}
              textStyle={styles.reconnectText}
              style={styles.reconnectButton}
              accessibilityLabel={`${reconnectLabel ?? "Reconnect"} ${serviceName}`}
            />
          ) : null}
          <View style={styles.chevronButton}>
            <ChevronDown size={18} color={colors.grey[500]} />
          </View>
        </View>
      </Pressable>

      {isExpanded && (
        <View style={styles.content}>
          <View style={styles.statsRow}>
            <View style={styles.statBlock}>
              <CustomText text="Connected" useTranslationText={false} style={styles.statLabel} />
              <CustomText text={connectedValue} useTranslationText={false} style={styles.statValue} />
            </View>
            <View style={styles.statBlock}>
              <CustomText text="Last synced" useTranslationText={false} style={styles.statLabel} />
              <CustomText text={lastSyncedValue} useTranslationText={false} style={styles.statValue} />
            </View>
          </View>

          <View style={styles.permissionsSection}>
            <CustomText text="Permissions" useTranslationText={false} style={styles.sectionLabel} />
            <View style={styles.permissionWrap}>{permissionPills}</View>
          </View>

          {onDisconnect ? (
            <CustomButton
              text={disconnectLabel ?? "Disconnect"}
              onPress={onDisconnect}
              useTranslationText={false}
              styleType="lightStroke"
              size="s"
              leftIcon={<Trash2 size={16} color={colors.error[600]} />}
              textStyle={styles.disconnectText}
              style={styles.disconnectButton}
              accessibilityLabel={`${disconnectLabel ?? "Disconnect"} ${serviceName}`}
            />
          ) : null}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.white,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "hidden",
  },
  header: {
    minHeight: 80,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
  },
  headerPressed: {
    backgroundColor: colors.grey[25],
  },
  headerHovered: {
    backgroundColor: colors.grey[50],
  },
  headerLeft: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    minWidth: 0,
  },
  logoShell: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.grey[100],
    alignItems: "center",
    justifyContent: "center",
  },
  logo: {
    width: 24,
    height: 24,
  },
  fallbackLogo: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.grey[100],
  },
  fallbackInitials: {
    color: colors.grey[700],
    fontSize: typographyScale.textMd.fontSize,
    lineHeight: typographyScale.textMd.lineHeight,
    fontWeight: "700",
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: spacing.xs,
  },
  serviceName: {
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  statusPill: {
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  accountLabel: {
    marginTop: 2,
    color: colors.grey[600],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  chevronButton: {
    width: 28,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    flexShrink: 0,
  },
  reconnectButton: {
    paddingHorizontal: spacing.md,
    borderRadius: radii.md,
  },
  reconnectText: {
    color: colors.white,
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  content: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    gap: spacing.md,
  },
  statsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    rowGap: spacing.sm,
    columnGap: spacing["2xl"],
  },
  statBlock: {
    minWidth: 160,
    flexGrow: 1,
  },
  statLabel: {
    color: colors.grey[600],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  statValue: {
    marginTop: 2,
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  permissionsSection: {
    gap: spacing.xs,
  },
  sectionLabel: {
    color: colors.grey[600],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  permissionWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.xs,
  },
  permissionPill: {
    paddingHorizontal: 10,
    paddingVertical: 1,
  },
  permissionText: {
    fontWeight: "500",
  },
  disconnectButton: {
    alignSelf: "flex-start",
    paddingHorizontal: spacing.sm,
    paddingVertical: 10,
    borderRadius: radii.md,
    justifyContent: "flex-start",
  },
  disconnectText: {
    color: colors.error[600],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
});

export type { ConnectedServiceAccordionProps, StatusTone };
